import { Link } from "react-router-dom";
import { CheckCircle2, Loader, Circle, ShieldCheck, FileText, ArrowRight, CalendarClock } from "lucide-react";

export const SOC2_LAST_UPDATED = "January 12, 2027";
import { PageLayout } from "@/components/PageLayout";

// NOTE: update `status` (done | current | upcoming) and dates as the program progresses.
const phases = [
  {
    status: "done",
    title: "Scoping & readiness planning",
    body: "Defined the systems, data flows, and Trust Services Criteria in scope, and set the roadmap for a SOC 2 Type II examination.",
  },
  {
    status: "current",
    title: "Gap assessment",
    body: "Mapping current controls against SOC 2 criteria to identify gaps across security, availability, and confidentiality.",
  },
  {
    status: "upcoming",
    title: "Control implementation & policies",
    body: "Rolling out policies, access controls, logging, vendor management, and secure-SDLC practices to close identified gaps.",
  },
  {
    status: "upcoming",
    title: "Evidence collection (observation period)",
    body: "Operating controls over a continuous observation window and collecting the evidence auditors require for a Type II report.",
  },
  {
    status: "upcoming",
    title: "Independent audit",
    body: "Engaging a licensed CPA firm to perform the SOC 2 examination.",
  },
  {
    status: "upcoming",
    title: "Report issued",
    body: "SOC 2 report available to customers and prospects under NDA.",
  },
];

const statusMeta = {
  done: { label: "Complete", Icon: CheckCircle2, cls: "text-green-400 border-green-400/40" },
  current: { label: "In progress", Icon: Loader, cls: "text-yellow-400 border-yellow-400/40" },
  upcoming: { label: "Upcoming", Icon: Circle, cls: "text-fog border-white/20" },
};

export default function Soc2Journey() {
  return (
    <PageLayout
      seoTitle="Our SOC 2 Journey | Aliwer"
      seoDescription="Aliwer is pursuing SOC 2 and ISO 27001. We're documenting our compliance journey in the open — because we hold our own security to the standard we set for clients."
      path="/trust/soc2-journey"
      eyebrow="Trust · in the open"
      title="Our SOC 2 journey."
      subtitle="We help companies get audit-ready — so we hold ourselves to the same bar, in public. Here's exactly where we are, updated as we progress."
      testid="soc2-journey-page"
    >
      <div className="mb-10" data-testid="soc2-last-updated">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-surface px-3.5 py-1.5 text-xs uppercase tracking-widest text-paper">
          <CalendarClock size={13} className="text-fog" />
          Last updated: {SOC2_LAST_UPDATED}
        </span>
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        {/* Timeline */}
        <div className="lg:col-span-2">
          <ol className="relative border-l border-white/10 ml-2" data-testid="soc2-timeline">
            {phases.map((p, i) => {
              const meta = statusMeta[p.status];
              const { Icon } = meta;
              return (
                <li key={i} className="relative pl-8 pb-9 last:pb-0" data-testid={`soc2-phase-${i}`}>
                  <span className={`absolute -left-[9px] top-0.5 grid place-items-center w-4 h-4 rounded-full bg-black border ${meta.cls.split(" ")[1]}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${p.status === "upcoming" ? "bg-fog/40" : meta.cls.split(" ")[0].replace("text-", "bg-")}`} />
                  </span>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="font-display text-lg tracking-tight text-paper">{p.title}</h2>
                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] uppercase tracking-widest ${meta.cls}`}>
                      <Icon size={12} /> {meta.label}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-fog leading-relaxed max-w-xl">{p.body}</p>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <div className="rounded-2xl border border-white/10 bg-surface p-6" data-testid="soc2-scope">
            <ShieldCheck size={20} className="text-fog" />
            <h3 className="mt-3 font-display text-base tracking-tight text-paper">Frameworks in scope</h3>
            <ul className="mt-3 space-y-2 text-sm text-paper/90">
              <li>SOC 2 (Type II) — in progress</li>
              <li>ISO 27001 — in progress</li>
              <li>GDPR / CCPA — privacy program in place</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-white/10 bg-surface p-6" data-testid="soc2-buyers">
            <FileText size={20} className="text-fog" />
            <h3 className="mt-3 font-display text-base tracking-tight text-paper">For prospective customers</h3>
            <p className="mt-3 text-sm text-fog leading-relaxed">
              Need our current status letter, security questionnaire, or a DPA for procurement? We're happy to share
              where we are and our timeline under NDA.
            </p>
            <Link
              to="/contact"
              data-testid="soc2-contact-cta"
              className="group mt-5 inline-flex items-center gap-2 rounded-full bg-paper text-black text-sm font-medium px-5 py-2.5 hover:bg-white transition-colors"
            >
              Request our security package
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </div>

      <p className="mt-10 text-xs text-fog/70 leading-relaxed max-w-2xl">
        This page reflects our status as of {SOC2_LAST_UPDATED} and is maintained for transparency. Certification
        timelines depend on the audit firm and observation period; we'll update each milestone as it's reached.
      </p>
    </PageLayout>
  );
}
